import { Component, computed, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Country, City, Zipcode, NumberLookupType, PhoneNumberType, RegulationInformation } from './cc-numbers.model';
import { CcNumbersService } from './cc-numbers.service';
import { AuthService } from '../../core/auth/auth.service';
import { RegulationAddressSnapshot, RegulationFormComponent } from './regulation-form/regulation-form.component';
import {
  addressCheckAppliesTo, addressCheckBlockReason, AddressCheckComponent, addressCheckDisablesSubmit,
  AddressCheckState, isAddressCheckBlocking,
} from './address-check/address-check.component';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmSpinnerComponent,
  HlmBadgeComponent,
  HLM_FORM_FIELD,
} from '../../ui';

export interface BookNumberDialogData {
  phoneServiceId?: number;
  brandId: number;
  /**
   * `fax` = book a fax number for a user account (old CMT `<number-dialog fax="true">`):
   * FAX inventory for the country/city lookups and no number-type choice. The result keeps the
   * callcenter shape; `CommunicationComponent` maps it onto a `FaxBookingRequest`.
   * Default `callcenter`.
   * @ticket EB-3809
   */
  mode?: 'callcenter' | 'fax';
}

export interface BookNumberDialogResult {
  phoneNumberData: {
    desiredCountryPrefix: string;
    desiredCityPrefix: string;
    desiredCityName: string;
    type: 'CALLCENTER' | 'HIDDEN';
    employeeId: string;
    regulationInformation?: any;
  };
  addressProofFile?: File;
  identityProofFile?: File;
}

const MAX_CITY_RESULTS = 50;
const MAX_COUNTRY_RESULTS = 30;

@Component({
  selector: 'cmt-book-number-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective,
    HlmSelectComponent, HlmSpinnerComponent,
    HlmBadgeComponent,
    RegulationFormComponent,
    AddressCheckComponent,
  ],
  template: `
    <hlm-dialog-surface size="xl">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon [name]="isFax ? 'lucideFileText' : 'lucidePhone'" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          @if (isFax) {
            <!-- Title copied from old CMT number.dialog.dir.js titles.fax -->
            <h2 hlmDialogTitle>Add additional fax number</h2>
            <p hlmDialogDescription>Book a fax number for this user account</p>
          } @else {
            <h2 hlmDialogTitle>Book New Number</h2>
            <p hlmDialogDescription>Add a phone number to this phone service</p>
          }
        </div>
        <button hlmDialogClose tabindex="-1" (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody class="max-h-[calc(100vh-200px)] overflow-y-auto">
        <form [formGroup]="form">

          <!-- Step 1: Location — Country + City side-by-side -->
          <div class="mb-2 flex items-center gap-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
            <span class="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-sm font-semibold leading-none text-primary-foreground"
              [class]="selectedCity() ? 'bg-success' : 'bg-primary'">1</span>
            Location
          </div>

          <div class="flex flex-wrap gap-3">
            <!-- relative: anchors the absolute clear-country button to this field, not the dialog -->
            <div class="relative min-w-0 flex-1 basis-[200px]">
              <hlm-form-field>
                <label hlmLabel>Country</label>
                <input hlmInput
                  formControlName="countrySearch"
                  [matAutocomplete]="countryAuto"
                  placeholder="Name or prefix..."
                  (input)="onCountrySearch()" />
                @if (countriesError()) {
                  <p hlmFieldError data-testid="book-number-countries-error">Countries could not be loaded — close and reopen the dialog to retry.</p>
                }
              </hlm-form-field>
              <mat-autocomplete #countryAuto="matAutocomplete"
                (optionSelected)="onCountrySelected($event)"
                [displayWith]="displayCountry">
                @for (c of filteredCountries(); track c.isoCountryCode) {
                  <mat-option [value]="c">
                    <span class="flex w-full items-center justify-between gap-2">
                      <span>{{ c.name }}</span>
                      <span class="font-mono-feat text-sm text-muted-foreground">{{ c.prefix }}</span>
                    </span>
                  </mat-option>
                }
                @if (filteredCountries().length === 0 && countrySearchValue()) {
                  <mat-option disabled>No match for "{{ countrySearchValue() }}"</mat-option>
                }
              </mat-autocomplete>
              @if (selectedCountry()) {
                <!-- before:-inset-2 widens the hit area to 44 px (touch target) without moving the 28 px icon -->
                <button hlmBtn variant="ghost" size="icon" class="absolute right-1 top-7 h-7 w-7 before:absolute before:-inset-2 before:content-['']" tabindex="-1"
                  aria-label="Clear country" data-testid="book-number-clear-country"
                  (click)="clearCountry($event)">
                  <hlm-icon name="lucideX" size="0.8rem" />
                </button>
              }
            </div>

            @if (selectedCountry()) {
              <div class="min-w-0 flex-1 basis-[200px]">
                <hlm-form-field>
                  <label hlmLabel>City</label>
                  @if (loadingCities()) {
                    <div class="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                      <hlm-spinner size="16" /> Loading...
                    </div>
                  } @else {
                    <input hlmInput #cityInput
                      formControlName="citySearch"
                      [matAutocomplete]="cityAuto"
                      placeholder="Name or prefix..."
                      (input)="onCitySearch()" />
                    <mat-autocomplete #cityAuto="matAutocomplete"
                      (optionSelected)="onCitySelected($event)"
                      [displayWith]="displayCity">
                      <!-- City prefixes are not unique (AUT "1" twice, BEL "60" = Charleroi + Chimay) → NG0955 with track c.prefix -->
                      @for (c of filteredCities(); track $index) {
                        <mat-option [value]="c">
                          <span class="flex items-center gap-2">
                            <span class="font-semibold text-primary">{{ c.prefix }}</span>
                            <span class="text-muted-foreground">|</span>
                            <span>{{ c.name }}</span>
                          </span>
                        </mat-option>
                      }
                      @if (filteredCities().length === 0 && citySearchValue()) {
                        <mat-option disabled>No match for "{{ citySearchValue() }}"</mat-option>
                      }
                    </mat-autocomplete>
                    @if (citiesError()) {
                      <p hlmFieldError data-testid="book-number-cities-error">Cities could not be loaded — select the country again to retry.</p>
                    }
                  }
                </hlm-form-field>
              </div>
            }
          </div>

          @if (selectedCountry() && cities().length > 0) {
            <div class="mt-2 flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-sm text-primary">
              <hlm-icon name="lucideInfo" size="0.8rem" />
              Found <strong class="mx-0.5">{{ cities().length }}</strong> cities for <strong class="mx-0.5">{{ selectedCountry()!.name }}.</strong>
              @if (cities().length > MAX_CITY_RESULTS) { Max {{ MAX_CITY_RESULTS }} shown — type to filter. }
            </div>
          }

          <!-- Step 2: Number details + Regulation -->
          @if (selectedCity()) {
            <div class="mb-2 mt-5 flex items-center gap-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
              <span class="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary text-sm font-semibold leading-none text-primary-foreground">2</span>
              Number details
            </div>

            <div class="flex flex-wrap items-center gap-3">
              <!-- Old CMT hides the number-settings panel in fax mode (number.dialog.html:50) -->
              @if (!isFax) {
                <div class="w-[200px]">
                  <hlm-select
                    [options]="[{value: 'CALLCENTER', label: 'CALLCENTER'}, {value: 'HIDDEN', label: 'HIDDEN'}]"
                    [value]="form.controls.type.value"
                    (valueChange)="form.controls.type.setValue($any($event) || 'CALLCENTER')"
                  />
                </div>
              }

              @if (!selectedCityRegulation()) {
                <hlm-badge variant="success">
                  <hlm-icon name="lucideCircleCheck" size="0.8rem" /> No regulation required
                </hlm-badge>
              } @else {
                <hlm-badge variant="accent">
                  <hlm-icon name="lucideScale" size="0.8rem" /> Regulation required — * = mandatory
                </hlm-badge>
              }
            </div>

            @if (selectedCityRegulation()) {
              <div class="mt-3">
                <cmt-regulation-form
                  [requirements]="selectedCityRegulation()"
                  [numberType]="form.controls.type.value"
                  [zipcodes]="zipcodes()"
                  [loadingZipcodes]="loadingZipcodes()"
                  [fixedCountryName]="selectedCountry()?.name ?? null"
                  (addressValueChange)="onAddressChange($event)"
                >
                  <cmt-address-check addressCheck
                    [isoCountryCode]="form.controls.countryIsoCode.value"
                    [cityPrefix]="form.controls.cityPrefix.value"
                    [address]="addressSnapshot()"
                    [enabled]="addressCheckEnabled()"
                    (stateChange)="addressCheckState.set($event)"
                    (correctionApplied)="regulationForm?.applyAddressCorrection($event)"
                  />
                </cmt-regulation-form>
              </div>
            }
          }
        </form>
      </section>

      <footer hlmDialogFooter>
        @if (bookDisabled() && blockReason(); as reason) {
          <!-- Only for a truly disabled Book (§17.2). Far left, BEFORE Cancel: the Cancel + Book pair
               keeps its right-aligned position whether or not a hint is shown (Style Guide §8). -->
          <span class="mr-auto text-sm text-foreground" data-testid="book-number-block-reason">{{ reason }}</span>
        }
        <button hlmBtn variant="outline" (click)="dialogRef.close()">Cancel</button>
        <button hlmBtn (click)="submit()" [disabled]="bookDisabled()" data-testid="book-number-submit">
          <hlm-icon [name]="isFax ? 'lucideFileText' : 'lucidePhone'" size="0.9rem" />
          {{ isFax ? 'Book Fax Number' : 'Book Number' }}
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class BookNumberDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<BookNumberDialogComponent>);
  readonly data: BookNumberDialogData = inject(MAT_DIALOG_DATA);
  private readonly api = inject(CcNumbersService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('cityInput') cityInput?: ElementRef<HTMLInputElement>;
  @ViewChild(RegulationFormComponent) regulationForm?: RegulationFormComponent;

  readonly MAX_CITY_RESULTS = MAX_CITY_RESULTS;
  readonly isFax = this.data.mode === 'fax';
  private readonly lookupType: NumberLookupType = this.isFax ? 'FAX' : 'CALLCENTER';

  readonly countries = signal<Country[]>([]);
  readonly cities = signal<City[]>([]);
  readonly selectedCountry = signal<Country | null>(null);
  readonly selectedCity = signal<City | null>(null);
  readonly selectedCityRegulation = signal<RegulationInformation | null>(null);
  readonly loadingCities = signal(false);
  readonly countriesError = signal(false);
  readonly citiesError = signal(false);
  /**
   * The in-flight city lookup. Cancelled on every country change — the FAX lookup takes ~15 s on
   * test, so a late response for the previous country would otherwise replace the current list.
   * @ticket EB-3809
   */
  private citiesSubscription?: Subscription;
  readonly zipcodes = signal<Zipcode[]>([]);
  readonly loadingZipcodes = signal(false);
  readonly countrySearchValue = signal('');
  readonly citySearchValue = signal('');

  readonly filteredCountries = computed(() => {
    const q = this.countrySearchValue().toLowerCase();
    const all = this.countries();
    if (!q) return all.slice(0, MAX_COUNTRY_RESULTS);
    return all.filter(c =>
      c.name.toLowerCase().includes(q) || c.prefix.includes(q),
    ).slice(0, MAX_COUNTRY_RESULTS);
  });

  readonly filteredCities = computed(() => {
    const q = this.citySearchValue().toLowerCase();
    const all = this.cities();
    if (!q) return all.slice(0, MAX_CITY_RESULTS);
    return all.filter(c =>
      c.name.toLowerCase().includes(q) || c.prefix.includes(q),
    ).slice(0, MAX_CITY_RESULTS);
  });

  readonly form = new FormGroup({
    countrySearch: new FormControl('', { nonNullable: true }),
    countryIsoCode: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    citySearch: new FormControl('', { nonNullable: true }),
    cityPrefix: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    type: new FormControl<PhoneNumberType>('CALLCENTER', { nonNullable: true }),
  });

  ngOnInit(): void {
    this.api.getCountries(this.data.brandId, this.lookupType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: c => this.countries.set(c),
        error: () => this.countriesError.set(true),
      });
  }

  // --- Country autocomplete ---

  displayCountry = (country: Country | string): string => {
    if (!country) return '';
    if (typeof country === 'string') return country;
    return `${country.name} (${country.prefix})`;
  };

  clearCountry(event: Event): void {
    event.stopPropagation();
    this.form.controls.countrySearch.reset('');
    this.form.controls.countryIsoCode.reset('');
    this.selectedCountry.set(null);
    this.countrySearchValue.set('');
    this.resetCityAndBelow();
  }

  clearCity(event: Event): void {
    event.stopPropagation();
    this.form.controls.citySearch.reset('');
    this.form.controls.cityPrefix.reset('');
    this.selectedCity.set(null);
    this.selectedCityRegulation.set(null);
    this.citySearchValue.set('');
    this.zipcodes.set([]);
    this.loadingZipcodes.set(false);
  }

  onCountrySearch(): void {
    const val = this.form.controls.countrySearch.value;
    this.countrySearchValue.set(typeof val === 'string' ? val : '');
    if (typeof val === 'string') {
      this.form.controls.countryIsoCode.setValue('');
      this.selectedCountry.set(null);
      this.resetCityAndBelow();
    }
  }

  onCountrySelected(event: any): void {
    const country: Country = event.option.value;
    this.form.controls.countryIsoCode.setValue(country.isoCountryCode);
    this.selectedCountry.set(country);
    this.countrySearchValue.set('');
    this.resetCityAndBelow();

    this.loadingCities.set(true);
    this.citiesSubscription = this.api.getCities(country.isoCountryCode, this.data.brandId, this.lookupType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: c => {
          this.cities.set(c);
          this.loadingCities.set(false);
          // Auto-focus city input to open dropdown
          setTimeout(() => this.cityInput?.nativeElement.focus(), 50);
        },
        error: () => {
          this.loadingCities.set(false);
          this.citiesError.set(true);
        },
      });
  }

  // --- City autocomplete ---

  displayCity = (city: City | string): string => {
    if (!city) return '';
    if (typeof city === 'string') return city;
    return `${city.prefix} | ${city.name}`;
  };

  onCitySearch(): void {
    const val = this.form.controls.citySearch.value;
    this.citySearchValue.set(typeof val === 'string' ? val : '');
    if (typeof val === 'string') {
      this.form.controls.cityPrefix.setValue('');
      this.selectedCity.set(null);
      this.selectedCityRegulation.set(null);
    }
  }

  onCitySelected(event: any): void {
    const city: City = event.option.value;
    this.form.controls.cityPrefix.setValue(city.prefix);
    this.selectedCity.set(city);
    this.selectedCityRegulation.set(city.regulationInformation ?? null);
    this.citySearchValue.set('');

    // Load zipcodes when the address form offers a ZIP field at all (it consumes them via
    // [zipcodes]). Presence, not `=== true`, is deliberately the right test here and must not be
    // "corrected" to match the address-check gate: the form renders a field it is merely offered
    // (hasAddrField), so an optional ZIP is on screen and still needs its suggestion list.
    // Tightening this would drop such a city to free-text entry and feed the geocoder worse data.
    const country = this.selectedCountry();
    if (country && 'zipCode' in (city.regulationInformation?.addressRequirements ?? {})) {
      this.loadingZipcodes.set(true);
      this.api.getZipcodes(country.isoCountryCode, city.prefix)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: z => {
            this.zipcodes.set(z);
            this.loadingZipcodes.set(false);
          },
          error: () => {
            this.zipcodes.set([]);
            this.loadingZipcodes.set(false);
          },
        });
    }
  }

  // --- Reset helpers ---

  private resetCityAndBelow(): void {
    // A still-running lookup for the previous country must not overwrite the next one's cities.
    this.citiesSubscription?.unsubscribe();
    this.citiesSubscription = undefined;
    this.citiesError.set(false);
    this.form.controls.citySearch.reset('');
    this.form.controls.cityPrefix.reset('');
    this.cities.set([]);
    this.selectedCity.set(null);
    this.selectedCityRegulation.set(null);
    this.citySearchValue.set('');
    this.zipcodes.set([]);
    this.loadingZipcodes.set(false);
  }

  // --- Address check (PS-6489) ---

  readonly addressSnapshot = signal<RegulationAddressSnapshot | null>(null);
  readonly addressCheckState = signal<AddressCheckState | null>(null);

  /** The geocode check runs only for German numbers (the carrier geocodes DEU only) and only where
   *  the city actually demands every field the check needs. Everywhere else it is not offered at
   *  all — not shown, and never gating the Book button. Scope rule shared with the repair dialog. */
  readonly addressCheckEnabled = computed(() => {
    if (this.form.controls.countryIsoCode.value !== 'DEU') return false;
    return addressCheckAppliesTo(this.selectedCityRegulation()?.addressRequirements);
  });

  onAddressChange(snapshot: RegulationAddressSnapshot): void {
    this.addressSnapshot.set(snapshot);
  }

  /**
   * Why Book is disabled — rendered beside it only while `bookDisabled()` (Style Guide §17.2).
   * Missing fields are never a footer hint: an incomplete form keeps Book clickable and a click
   * reveals them (`submit`). Covers exactly the disabled states: no city, check running, blocking
   * verdict.
   * @ticket EB-3809
   */
  blockReason(): string | null {
    if (!this.selectedCity()) return 'Select a country and a city to continue';
    if (!this.addressCheckEnabled()) return null;
    return addressCheckBlockReason(this.addressCheckState(), 'enable booking');
  }

  canSubmit(): boolean {
    if (!this.form.controls.countryIsoCode.value || !this.form.controls.cityPrefix.value) return false;
    if (!this.form.valid) return false;
    // The shared regulation form gates its own validity (mandatory fields + required proof files).
    if (this.selectedCityRegulation() && !this.regulationForm?.isValid()) return false;
    // Geocode verdict gate. Fail-open: an unreachable provider does not block (PS-6483).
    if (this.addressCheckState()?.status !== 'inactive' && this.addressCheckBlocked()) return false;
    return true;
  }

  private addressCheckBlocked(): boolean {
    if (!this.addressCheckEnabled()) return false;
    return isAddressCheckBlocking(this.addressCheckState());
  }

  /**
   * Book stays clickable on an incomplete form (submit-to-reveal, see `submit`). It is disabled
   * only where a click cannot help: no city chosen yet, the address check still running, or a
   * blocking verdict the operator must act on in the check panel.
   * @ticket EB-3809
   */
  bookDisabled(): boolean {
    if (!this.selectedCity()) return true;
    return this.addressCheckEnabled() && addressCheckDisablesSubmit(this.addressCheckState());
  }

  // --- Submit ---

  submit(): void {
    if (!this.canSubmit()) {
      this.revealBlocker();
      return;
    }

    const city = this.selectedCity();
    const country = this.selectedCountry();

    const result: BookNumberDialogResult = {
      phoneNumberData: {
        desiredCountryPrefix: country?.prefix ?? '',
        desiredCityPrefix: this.form.controls.cityPrefix.value,
        desiredCityName: city?.name ?? '',
        type: this.form.controls.type.value as 'CALLCENTER' | 'HIDDEN',
        employeeId: String(this.auth.currentUserId),
      },
    };

    if (this.selectedCityRegulation()) {
      const regValue = this.regulationForm?.getValue();
      if (regValue) {
        result.phoneNumberData.regulationInformation = regValue.regulationInformation;
        if (regValue.addressProofFile) result.addressProofFile = regValue.addressProofFile;
        if (regValue.identityProofFile) result.identityProofFile = regValue.identityProofFile;
      }
    }

    this.dialogRef.close(result);
  }

  /**
   * A click on Book with an incomplete form shows what is missing instead of doing nothing: the
   * regulation form outlines, lists and scrolls to the missing fields. (Address-check verdicts
   * disable Book instead — see `bookDisabled`.)
   * @ticket EB-3809
   */
  private revealBlocker(): void {
    if (this.selectedCityRegulation() && !this.regulationForm?.isValid()) this.regulationForm?.revealMissing();
  }
}
